import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Check } from 'lucide-react';

// Conversation starter definitions with categories
export const CONVERSATION_STARTERS = [
  // Analysis & Questions - Gray
  {
    id: 'main-topics',
    label: 'What are the main topics?',
    prompt: 'What are the main topics discussed?',
    category: 'analysis',
    color: 'gray'
  },
  {
    id: 'summarize',
    label: 'Summarize this content',
    prompt: 'Summarize this content in bullet points',
    category: 'analysis',
    color: 'gray'
  },
  {
    id: 'external-references',
    label: 'External references for visuals',
    prompt: 'List external references made which we should show visuals for',
    category: 'analysis',
    color: 'gray'
  },
  {
    id: 'table-of-contents',
    label: 'Table of contents',
    prompt: 'Can you create a table of contents using only HH:MM:SS (not using timecode links), dont be too verbose, about 6-7 items per hour, less is more, be concise with the title names',
    category: 'analysis',
    color: 'gray'
  },
  {
    id: 'custom-vocab',
    label: 'Custom Vocabulary',
    prompt: 'Give me a custom vocab in comma delimited format for words in this show likely to be mistranscribed, note that our show is MLST, our Sponsor is Tufa AI Labs (lab director Benjamin Crouzier)',
    category: 'analysis',
    color: 'gray'
  },

  // Improvements & Recommendations - Green
  {
    id: 'top-5-improvements',
    label: 'Top 5 improvements',
    prompt: 'Suggest the top 5 actions to improve the show',
    category: 'improvements',
    color: 'green'
  },
  {
    id: 'lessons-clips',
    label: 'Lessons & clip ideas',
    prompt: "Analyze this published video's YouTube performance to extract lessons learned. Focus on: 1) What worked well and why (high retention segments, engagement spikes), 2) What didn't work and why (drop-off points, low engagement), 3) Identify the strongest moments that could be repurposed as YouTube Shorts or Twitter mid-forms - use the topic map to find thematic clips from different parts of the show. For each clip suggestion, explain how to frame/present it for maximum impact. The goal is learning from this video's performance to improve future content.",
    category: 'improvements',
    color: 'green'
  },
  {
    id: 'top-5-trimming',
    label: 'Top 5 trimming recommendations',
    prompt: 'Provide top 5 recommendations for trimming content - identify specific segments to cut to improve pacing and viewer engagement',
    category: 'improvements',
    color: 'green'
  },
  {
    id: 'top-5-rearranging',
    label: 'Top 5 re-arranging recommendations',
    prompt: 'Provide top 5 recommendations for re-arranging content - suggest better sequencing and flow to maximize impact and viewer retention',
    category: 'improvements',
    color: 'green'
  },
  {
    id: 'youtube-shorts',
    label: '5 YouTube Shorts candidates',
    prompt: 'Give me 5 suggested YouTube short candidates, comprised of subclips, hook, title and visuals to display',
    category: 'improvements',
    color: 'green'
  },

  // YouTube Analysis - Red
  {
    id: 'youtube-retention',
    label: 'YouTube retention/stats analysis',
    prompt: "Perform a detailed analysis of the YouTube retention and stats on this video. Analyze the retention curve, identify where viewers drop off, correlate with the transcript content at those timestamps, and provide insights on what's working and what's not.",
    category: 'youtube',
    color: 'red'
  },

  // Narrative Analysis - Purple
  {
    id: 'narrative-analysis',
    label: 'Perform narrative analysis with improvements',
    prompt: 'Perform a narrative analysis on the content with suggestions on improvements',
    category: 'narrative',
    color: 'purple'
  },
  {
    id: 'captivating-story',
    label: 'How to edit into a captivating story',
    prompt: 'How could I edit this into a captivating story, give suggestions',
    category: 'narrative',
    color: 'purple'
  },
  {
    id: 'viral-titles',
    label: '20 viral titles and thumbnail concepts',
    prompt: 'Please come up with 20 viral titles and thumbnail concepts for YouTube',
    category: 'narrative',
    color: 'purple'
  },
  {
    id: 'synopsis',
    label: 'Synopsis with guest names',
    prompt: 'Can you generate a synopsis of the show in simple relatable language, starting with the guests names',
    category: 'narrative',
    color: 'purple'
  },

  // Critical Issues - Red
  {
    id: 'weakest-parts',
    label: 'Weakest parts for YouTube tech audience',
    prompt: 'What are the weakest parts of this content for YouTube technical audience?',
    category: 'critical',
    color: 'red'
  },
  {
    id: 'factual-accuracy',
    label: 'Critically appraise for factual accuracy',
    prompt: 'Can you critically appraise the content for factual accuracy',
    category: 'critical',
    color: 'red'
  },
  {
    id: 'weak-questions',
    label: 'Critique weak questions & errors',
    prompt: 'Can you critically appraise weak questions and point out anything the hosts said which is wrong or embarrassing',
    category: 'critical',
    color: 'red'
  },
  {
    id: 'host-critique',
    label: 'Critique host contributions & suggest trims',
    prompt: "Critically appraise all of the host's questions and contributions for factual correctness. Identify any errors, misunderstandings, or weak points. Also provide specific suggestions on how to trim down the host's contributions to improve pacing and keep focus on the guest's expertise. Be direct and constructive.",
    category: 'critical',
    color: 'red'
  },

  // Editing Markers - Orange
  {
    id: 'editing-markers',
    label: 'Find editing markers (stumbles, interruptions)',
    prompt: 'Search through the transcript and find all instances where: 1) Someone says "let\'s edit that out" or similar phrases indicating content should be removed, 2) There are obvious stumbles, false starts, or someone asking to restart, 3) There are interruptions or talking over each other, 4) Someone says "the guest" when referring to themselves (third person mistakes), 5) Any "um", "uh", long pauses, or filler words that indicate potential edit points. For each instance, provide the timecode and a brief description of what was said.',
    category: 'editing',
    color: 'orange'
  },

  // Visual Opportunities - Blue
  {
    id: 'visual-opportunities',
    label: 'Find visual/B-roll opportunities',
    prompt: `Search through the transcript and identify all moments where adding a visual, image, diagram, or B-roll would significantly enhance viewer understanding. Look for:

1. **Papers & Publications**: When a specific research paper, study, or publication is mentioned by name or author
2. **Books**: Any book titles, authors, or specific chapters referenced
3. **Diagrams & Figures**: When someone describes a figure, chart, graph, or visual concept from their work
4. **Algorithms & Code**: When specific algorithms, equations, code snippets, or technical implementations are explained
5. **Products & Tools**: Specific software, hardware, websites, or tools mentioned
6. **People**: When notable researchers, scientists, or public figures are discussed
7. **Concepts**: Complex technical concepts that would benefit from a visual explanation
8. **Historical Events**: References to historical moments, experiments, or breakthroughs
9. **Organizations**: Companies, labs, universities, or institutions mentioned

For each opportunity, provide as a list:
- HH:MM:SS - [Brief context of what's being discussed] → Suggested visual: [Specific description of what to show]

Be specific about what visual would help - don't just say "add an image", describe exactly what should be shown (e.g., "Screenshot of the GPT-4 paper abstract", "Photo of Geoffrey Hinton", "Diagram of transformer attention mechanism").`,
    category: 'visuals',
    color: 'blue'
  },

  // Sensitivity Analysis - Yellow
  {
    id: 'sensitivity-analysis',
    label: 'Corporate/PR sensitivity analysis',
    prompt: 'Run corporate/PR/comms sensitivity analysis: identify confidential information, legal risks, ethical concerns, or anything likely to get blocked in review',
    category: 'sensitivity',
    color: 'yellow'
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  analysis: 'Analysis & Questions',
  improvements: 'Improvements & Recommendations',
  youtube: 'YouTube Analysis',
  narrative: 'Narrative Analysis',
  critical: 'Critical Issues',
  editing: 'Editing Markers',
  visuals: 'Visual Opportunities',
  sensitivity: 'Sensitivity Analysis',
};

const COLOR_CLASSES: Record<string, { bg: string; hover: string; border: string; text: string }> = {
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    hover: 'hover:bg-gray-200 dark:hover:bg-gray-600',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-700 dark:text-gray-300',
  },
  green: {
    bg: 'bg-[#FFF4D8] dark:bg-[#332A17]',
    hover: 'hover:bg-[#FFE7AD] dark:hover:bg-[#43331B]',
    border: 'border-[#E7B14C] dark:border-[#8E6E33]',
    text: 'text-[#8B5B00] dark:text-[#FFE3A1]',
  },
  red: {
    bg: 'bg-red-100 dark:bg-red-800',
    hover: 'hover:bg-red-200 dark:hover:bg-red-700',
    border: 'border-red-300 dark:border-red-600',
    text: 'text-red-700 dark:text-red-200',
  },
  purple: {
    bg: 'bg-[#EEF1F3] dark:bg-[#232E3F]',
    hover: 'hover:bg-[#E1E7EE] dark:hover:bg-[#2F3B4E]',
    border: 'border-[#445166] dark:border-[#556274]',
    text: 'text-[#232E3F] dark:text-[#F4F4F4]',
  },
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-800',
    hover: 'hover:bg-yellow-200 dark:hover:bg-yellow-700',
    border: 'border-yellow-300 dark:border-yellow-600',
    text: 'text-yellow-700 dark:text-yellow-200',
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-800',
    hover: 'hover:bg-orange-200 dark:hover:bg-orange-700',
    border: 'border-orange-300 dark:border-orange-600',
    text: 'text-orange-700 dark:text-orange-200',
  },
  blue: {
    bg: 'bg-[#EEF1F3] dark:bg-[#232E3F]',
    hover: 'hover:bg-[#E1E7EE] dark:hover:bg-[#2F3B4E]',
    border: 'border-[#445166] dark:border-[#556274]',
    text: 'text-[#232E3F] dark:text-[#F4F4F4]',
  },
};

interface BatchGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (prompts: Array<{ prompt: string }>) => void;
  selectedModel: string;
  isStarting?: boolean;
}

export const BatchGenerateModal: React.FC<BatchGenerateModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  selectedModel,
  isStarting = false,
}) => {
  const [selectedStarters, setSelectedStarters] = useState<Set<string>>(new Set());

  // Group starters by category
  const startersByCategory = CONVERSATION_STARTERS.reduce((acc, starter) => {
    if (!acc[starter.category]) {
      acc[starter.category] = [];
    }
    acc[starter.category].push(starter);
    return acc;
  }, {} as Record<string, typeof CONVERSATION_STARTERS>);

  const toggleStarter = (id: string) => {
    const newSelected = new Set(selectedStarters);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedStarters(newSelected);
  };

  const selectAll = () => {
    setSelectedStarters(new Set(CONVERSATION_STARTERS.map(s => s.id)));
  };

  const deselectAll = () => {
    setSelectedStarters(new Set());
  };

  const handleGenerate = () => {
    if (selectedStarters.size === 0) return;

    const selectedPrompts = CONVERSATION_STARTERS
      .filter(s => selectedStarters.has(s.id))
      .map(s => ({ prompt: s.prompt }));

    onGenerate(selectedPrompts);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[9998]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Batch Generate Conversations
                </h3>
                <button
                  onClick={onClose}
                  disabled={isStarting}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Selection Controls */}
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select conversation starters to generate. Each will create a separate conversation.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="rounded px-2 py-1 text-xs bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD] dark:bg-[#332A17] dark:text-[#FFE3A1] dark:hover:bg-[#43331B]"
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAll}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Starters grouped by category */}
                {Object.entries(startersByCategory).map(([category, starters]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {CATEGORY_LABELS[category] || category}
                    </h4>
                    <div className="space-y-2">
                      {starters.map(starter => {
                        const isSelected = selectedStarters.has(starter.id);
                        const colors = COLOR_CLASSES[starter.color];
                        return (
                          <button
                            key={starter.id}
                            onClick={() => toggleStarter(starter.id)}
                            className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                              isSelected
                                ? `${colors.bg} ${colors.border} ring-2 ring-offset-1 ring-[#FFB52B]`
                                : `bg-white dark:bg-gray-700 ${colors.border} ${colors.hover}`
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                                isSelected
                                  ? 'bg-[#FFB52B] border-[#FFB52B]'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className={`text-sm ${colors.text}`}>
                                {starter.label}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedStarters.size} starter{selectedStarters.size !== 1 ? 's' : ''} selected
                  {selectedStarters.size > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Model: {selectedModel.split('/').pop()})
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    disabled={isStarting}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={selectedStarters.size === 0 || isStarting}
                    className="flex items-center gap-2 rounded-md bg-[#FFB52B] px-4 py-2 text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Play className="w-4 h-4" />
                    {isStarting ? 'Starting...' : `Generate ${selectedStarters.size} Conversation${selectedStarters.size !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
